import { useRef, useState } from 'react';
import { linkHostname, normalizeLinkArtifactUrl } from '@bower/shared/utils/linkHref';

import { useUrlMetadata } from '../../../hooks/data/useUrlMetadata';
import { useDebounce } from '../../../hooks/ui/useDebounce';
import { fetchUrlMetadata, type UrlMetadata } from '../../../services/urlMetadata.service';
import {
  chooseLinkTitle,
  chooseSiteIconUrl,
  type LinkFieldValues,
  type LinkIconChoice,
  type LinkTitleSource,
} from './linkPrefill';

/** How long the URL must sit still before the page is looked up. */
const LOOKUP_DEBOUNCE_MS = 400;

/** The inline error, once the user has typed something that is not a page URL. */
function urlError(touched: boolean, url: string, normalized: string | null): string | null {
  return touched && url.trim() && !normalized ? 'Enter a web address (https://...)' : null;
}

/** An existing link's icon choice: its stored site icon, or Bower's. */
function initialIconChoice(initial: LinkFieldValues | undefined): LinkIconChoice | null {
  if (!initial) return null;
  return initial.favicon ? 'site' : 'bower';
}

/**
 * The link form's state, shared by create and edit.
 *
 * The prefill waits: the site icon is derived only from the debounced URL on
 * screen, and the title only once the resolver has answered for it, so a
 * half-typed address never fills the title or loads an icon from a partial
 * host. Until then the title field is empty with the hostname as its
 * placeholder (and as the saved fallback). Once the user types a title it is
 * theirs: nothing writes to it again except "Refresh from page".
 *
 * `initial` is an existing link (edit): its title counts as the user's, its
 * stored icon stands until the URL changes or the user refreshes, and the
 * page is looked up only once there is something new to look up.
 */
export function useLinkFields(initial?: LinkFieldValues) {
  const [url, setUrlValue] = useState(initial?.url ?? '');
  /** null = not typed: the title follows the page. */
  const [userTitle, setUserTitle] = useState<string | null>(initial?.title ?? null);
  /** null = the default: the site icon whenever there is one. */
  const [iconChoice, setIconChoice] = useState(initialIconChoice(initial));
  const [refreshed, setRefreshed] = useState(false);
  const [urlTouched, setUrlTouched] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const resolving = useRef(false);

  const normalizedUrl = normalizeLinkArtifactUrl(url);
  const debouncedUrl = useDebounce(normalizedUrl, LOOKUP_DEBOUNCE_MS);
  const isNewUrl = !initial || normalizedUrl !== initial.url || refreshed;
  const metadataQuery = useUrlMetadata(isNewUrl ? debouncedUrl : null);
  // Settled: the page's answer for the URL on screen, once typing has paused.
  const settledUrl = isNewUrl && debouncedUrl === normalizedUrl ? debouncedUrl : null;
  const metadata = settledUrl ? metadataQuery.data : undefined;
  const siteIcon = siteIconFor(isNewUrl, settledUrl, metadata, normalizedUrl, initial);
  const hostname = normalizedUrl ? linkHostname(normalizedUrl) : '';
  const { title, titleSource, titleInput } = titleFor(userTitle, settledUrl, metadata, hostname);
  const icon: LinkIconChoice = iconChoice ?? (siteIcon ? 'site' : 'bower');
  const values = toValues(normalizedUrl, title, icon === 'site' ? siteIcon : null);

  /**
   * What Save writes, for the URL on screen NOW. Until typing pauses and the
   * page answers, the site icon and the prefilled title are missing (Save
   * inside the debounce once stored favicon null while the form said "Site
   * icon"), so this looks the final URL up first. The lookup keeps its own
   * cache, so a URL the form already settled costs nothing.
   */
  const resolveValues = async (): Promise<ResolvedLinkValues | null> => {
    if (!normalizedUrl || !values || resolving.current) return null;
    const answered = settledUrl !== null && metadataQuery.data !== undefined;
    if (!isNewUrl || answered) return { values, titleSource };
    resolving.current = true;
    setIsResolving(true);
    try {
      const page = await fetchUrlMetadata(normalizedUrl);
      const pageIcon = chooseSiteIconUrl(normalizedUrl, page);
      const resolved = titleFor(userTitle, normalizedUrl, page, hostname);
      const choice = iconChoice ?? (pageIcon ? 'site' : 'bower');
      return {
        values: {
          url: normalizedUrl,
          title: resolved.title,
          favicon: choice === 'site' ? pageIcon : null,
        },
        titleSource: resolved.titleSource,
      };
    } finally {
      resolving.current = false;
      setIsResolving(false);
    }
  };

  return {
    url,
    setUrl: (value: string) => {
      setUrlValue(value);
      setUrlTouched(true);
    },
    urlError: urlError(urlTouched, url, normalizedUrl),
    normalizedUrl,
    /** What the title field shows: the user's text, else the settled prefill. */
    titleInput,
    /** The field's placeholder: the hostname the title falls back to. */
    titlePlaceholder: hostname || 'Filled in from the page',
    setTitle: setUserTitle,
    title,
    titleSource,
    icon,
    setIcon: setIconChoice,
    siteIcon,
    isLookingUp: isNewUrl && debouncedUrl !== null && metadataQuery.isFetching,
    /** Re-run the prefill: the page's title and icon replace what is there. */
    refresh: () => {
      setUserTitle(null);
      setIconChoice(null);
      setRefreshed(true);
    },
    /** The values on screen, or null while the URL is not a valid page. Save uses resolveValues. */
    values,
    resolveValues,
    /** Save is looking the final URL up before it writes. */
    isResolving,
  };
}

/**
 * The title: the user's text once they have typed (even emptied), else the
 * page's prefill once the lookup has settled, else the hostname. The field
 * shows only the first two; the hostname is its placeholder.
 */
function titleFor(
  userTitle: string | null,
  settledUrl: string | null,
  metadata: UrlMetadata | undefined,
  hostname: string,
): { readonly title: string; readonly titleSource: LinkTitleSource; readonly titleInput: string } {
  const prefilled = settledUrl && metadata ? chooseLinkTitle(settledUrl, metadata) : null;
  const typed = userTitle?.trim() ?? '';
  return {
    title: typed || prefilled?.title || hostname,
    titleSource: typed ? 'user' : (prefilled?.source ?? 'hostname'),
    titleInput: userTitle ?? prefilled?.title ?? '',
  };
}

/**
 * The site icon on offer. An unchanged existing link keeps its stored icon (or
 * its site's /favicon.ico when it has none, so "Site icon" stays choosable). A
 * new URL offers one only once typing has paused on a valid URL (its
 * /favicon.ico, then the page's own https icon once the lookup answers):
 * never per keystroke, so no partial host is ever requested.
 */
function siteIconFor(
  isNewUrl: boolean,
  settledUrl: string | null,
  metadata: UrlMetadata | undefined,
  normalizedUrl: string | null,
  initial: LinkFieldValues | undefined,
): string | null {
  if (!isNewUrl) {
    return initial?.favicon ?? (normalizedUrl ? chooseSiteIconUrl(normalizedUrl, undefined) : null);
  }
  return settledUrl ? chooseSiteIconUrl(settledUrl, metadata) : null;
}

function toValues(
  url: string | null,
  title: string,
  favicon: string | null,
): LinkFieldValues | null {
  return url ? { url, title, favicon } : null;
}

/** What Save writes: the values, and where the title came from (Link Created). */
export interface ResolvedLinkValues {
  readonly values: LinkFieldValues;
  readonly titleSource: LinkTitleSource;
}

export type LinkFieldsState = ReturnType<typeof useLinkFields>;
